import { createStepFromJson, executeSteps, FormActionFrameworkStep, registerStep, } from '../step';
import { resolveTemplate } from '../template';
/** Hard cap so a bad step size / range cannot hang the browser. */
const MAX_ITERATIONS = 1000;
/**
 * Numeric range loop container.
 * Sets `as` (default `index`) on context.variables each iteration, then runs nested body steps.
 * Body is stored in JSON as `then` (same slot as IfStep's then branch).
 */
export class ForeachStep extends FormActionFrameworkStep {
    constructor() {
        super(...arguments);
        Object.defineProperty(this, "type", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 'foreach'
        });
        Object.defineProperty(this, "config", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                from: '0',
                to: '0',
                step: '1',
                as: 'index',
            }
        });
        Object.defineProperty(this, "bodySteps", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
    }
    async execute(context) {
        const from = toNumber(resolveTemplate(this.config.from ?? 0, context), 'from', context);
        const to = toNumber(resolveTemplate(this.config.to ?? 0, context), 'to', context);
        const stepSize = toNumber(resolveTemplate(this.config.step ?? 1, context), 'step', context);
        if (from == null || to == null || stepSize == null) {
            return context;
        }
        if (stepSize === 0) {
            context.log('[foreach] step size is 0 — skipped');
            return context;
        }
        const asRaw = (typeof this.config.as === 'string' && this.config.as.trim()) || 'index';
        const as = String(resolveTemplate(asRaw, context) ?? 'index').trim() || 'index';
        if (this.bodySteps.length === 0) {
            context.log(`[foreach] ${as}=${from}..${to} step ${stepSize} — empty body`);
            return context;
        }
        const hadPrevious = context.variables.has(as);
        const previous = context.variables.get(as);
        let iterations = 0;
        context.log(`[foreach] ${as}=${from}..${to} step ${stepSize} (${this.bodySteps.length} body step(s))`);
        for (let i = from; stepSize > 0 ? i <= to : i >= to; i += stepSize) {
            if (context.shouldStop) {
                context.log(`[foreach] stopped at ${as}=${i}`);
                break;
            }
            if (iterations >= MAX_ITERATIONS) {
                context.log(`[foreach] hit max iterations (${MAX_ITERATIONS}) — stopping`);
                break;
            }
            context.variables.set(as, i);
            context.log(`[foreach] ${as}=${i}`);
            await executeSteps(this.bodySteps, context);
            iterations += 1;
        }
        if (hadPrevious) {
            context.variables.set(as, previous);
        }
        else {
            context.variables.delete(as);
        }
        context.log(`[foreach] done — ${iterations} iteration(s)`);
        return context;
    }
    toJSON() {
        return {
            type: this.type,
            name: this.name || undefined,
            config: { ...this.config },
            then: this.bodySteps.map((s) => s.toJSON()),
        };
    }
    fromJSON(json) {
        super.fromJSON(json);
        const record = json;
        this.bodySteps = Array.isArray(record.then)
            ? record.then.map((child) => createStepFromJson(child))
            : [];
    }
    showStepForm(container, _context, onUpdated) {
        const cfg = this.config;
        container.innerHTML = `
      <div class="mb-3">
        <label class="form-label">Step name</label>
        <input type="text" class="form-control" data-faf="name" value="${escapeAttr(this.name)}" />
      </div>
      <div class="faf-id-row mb-3">
        <div class="faf-id-field">
          <label class="form-label">From (inclusive)</label>
          <input type="text" class="form-control" data-faf="from" value="${escapeAttr(cfg.from)}" placeholder="10" />
        </div>
        <div class="faf-id-field">
          <label class="form-label">To (inclusive)</label>
          <input type="text" class="form-control" data-faf="to" value="${escapeAttr(cfg.to)}" placeholder="15" />
        </div>
        <div class="faf-id-field">
          <label class="form-label">Step</label>
          <input type="text" class="form-control" data-faf="step" value="${escapeAttr(cfg.step ?? '1')}" placeholder="1" />
        </div>
      </div>
      <div class="mb-3">
        <label class="form-label">Index variable name</label>
        <input type="text" class="form-control" data-faf="as" value="${escapeAttr(cfg.as ?? 'index')}" placeholder="index" />
        <div class="text-muted small">
          Set each iteration; use in nested steps as <code>{{index}}</code>
          (or your name). Example field: <code>field[{{index}}]</code> → field[10], field[11], …
          Nested body steps are edited in the container below.
        </div>
      </div>
    `;
        const sync = () => {
            this.name = inputValue(container, 'name');
            this.config.from = inputValue(container, 'from');
            this.config.to = inputValue(container, 'to');
            this.config.step = inputValue(container, 'step') || '1';
            this.config.as = inputValue(container, 'as') || 'index';
            onUpdated?.();
        };
        container.querySelectorAll('input').forEach((el) => {
            el.addEventListener('change', sync);
            el.addEventListener('blur', sync);
        });
    }
}
function toNumber(value, label, context) {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === 'string' && value.trim() !== '') {
        const n = Number(value.trim());
        if (Number.isFinite(n))
            return n;
    }
    context.log(`[foreach] invalid ${label}: ${String(value)} — skipped`);
    return null;
}
function inputValue(container, key) {
    const el = container.querySelector(`[data-faf="${key}"]`);
    return el?.value?.trim() ?? '';
}
function escapeAttr(value) {
    if (value == null)
        return '';
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;');
}
registerStep('foreach', ForeachStep);
